import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import useProfileImage from '../../hooks/useProfileImage';
import { 
  LayoutDashboard, Users, Calendar, CreditCard, FileText, Bell, 
  Menu, X, LogOut, Settings, ChevronDown, UserCircle, Building2, Megaphone,
  PartyPopper, RefreshCw, FileSpreadsheet, ClipboardList, UserCheck, Crown, MessageSquare, ShoppingCart, HelpCircle, Lock, Camera, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import NotificationBell from '../notifications/NotificationBell';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map navigation paths to feature keys for dynamic permission check
const navFeatureMapping = {
  '/organizer': null, // Always accessible
  '/organizer/events': null, // Always accessible
  '/organizer/attendees': 'viewAttendees',
  '/organizer/members': 'viewAttendees',
  '/organizer/committees': 'viewCommittees',
  '/organizer/announcements': 'createAnnouncements',
  '/organizer/payments': 'viewPayments',
  '/organizer/documents': 'uploadDocuments',
  '/organizer/program': 'viewProgram',
  '/organizer/tasks': 'viewTasks',
  '/organizer/checkin': 'viewCheckin',
  '/organizer/reports': 'viewReports',
  '/organizer/support': 'viewSupport',
  '/organizer/messages': null, // Always accessible
  '/organizer/gallery': 'viewGallery', // Photo gallery
  '/organizer/scheduled-reports': 'viewReports', // Scheduled reports use same permission as reports
  '/organizer/cart': null, // Always accessible
  '/organizer/help': 'viewHelp',
  '/organizer/subscription': null, // Always accessible
};

const OrganizerLayout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const { currentTier, loading: subLoading, hasFeature } = useSubscription();
  const profileImageUrl = useProfileImage(user);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);

  // Check if user can access a route based on dynamic Feature Matrix permissions
  const canAccessRoute = (path) => {
    const featureKey = navFeatureMapping[path];
    if (!featureKey) return true; // No feature requirement, always accessible
    return hasFeature(featureKey);
  };

  const handleNavClick = (e, path, canAccess) => {
    if (!canAccess) {
      e.preventDefault();
      toast.info('Upgrade your subscription to access this feature');
      navigate('/organizer/subscription');
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchCartCount();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
      // Select first event by default if any exist
      if (response.data.length > 0 && !selectedEvent) {
        setSelectedEvent(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchCartCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organizer/cart`);
      setCartCount(response.data.items?.length || 0);
    } catch (error) {
      // Cart might not exist
    }
  };

  // Fetch event logo
  useEffect(() => {
    const fileId = selectedEvent ? selectedEvent.logo_file_id : null;
    if (fileId && token) {
      axios.get(`${API_URL}/api/files/download/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }).then(res => {
        setLogoUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(res.data);
        });
      }).catch(() => {});
    } else {
      setLogoUrl(null);
    }
  }, [selectedEvent, token]);

  const navigation = [
    { name: 'Dashboard', href: '/organizer', icon: LayoutDashboard },
    { name: 'My Events', href: '/organizer/events', icon: PartyPopper },
    { name: 'Attendees', href: '/organizer/attendees', icon: Users },
    { name: 'Committees', href: '/organizer/committees', icon: Building2 },
    { name: 'Announcements', href: '/organizer/announcements', icon: Megaphone },
    { name: 'Documents', href: '/organizer/documents', icon: FileText },
    { name: 'Payments', href: '/organizer/payments', icon: CreditCard },
    { name: 'Program', href: '/organizer/program', icon: Calendar },
    { name: 'Tasks', href: '/organizer/tasks', icon: ClipboardList },
    { name: 'Check-in', href: '/organizer/checkin', icon: UserCheck },
    { name: 'Gallery', href: '/organizer/gallery', icon: Camera },
    { name: 'Reports', href: '/organizer/reports', icon: FileSpreadsheet },
    { name: 'Scheduled Reports', href: '/organizer/scheduled-reports', icon: Clock },
    { name: 'Messages', href: '/organizer/messages', icon: MessageSquare },
    { name: 'Cart', href: '/organizer/cart', icon: ShoppingCart },
    { name: 'Support', href: '/organizer/support', icon: Bell },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Mobile menu button */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-maroon px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/organizer" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">b8</span>
            </div>
            <span className="font-heading font-bold text-xl text-white">Organizer</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/organizer/cart')}
              className="text-white hover:bg-white/10 relative"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold text-maroon text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:bg-white/10"
              data-testid="organizer-mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-16 overflow-y-auto">
          <nav className="px-4 py-6 space-y-2 pb-32">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const canAccess = canAccessRoute(item.href);
              return (
                <Link
                  key={item.name}
                  to={canAccess ? item.href : '#'}
                  onClick={(e) => {
                    if (!canAccess) {
                      e.preventDefault();
                      setMobileMenuOpen(false);
                      toast.info('Upgrade to access this feature');
                      navigate('/organizer/subscription');
                    } else {
                      setMobileMenuOpen(false);
                    }
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-maroon text-white' 
                      : canAccess
                        ? 'text-gray-700 hover:bg-cream-paper'
                        : 'text-gray-400 bg-gray-50'
                  }`}
                >
                  <item.icon className={`h-5 w-5 ${!canAccess ? 'opacity-50' : ''}`} />
                  <span className={`font-medium flex-1 ${!canAccess ? 'opacity-50' : ''}`}>{item.name}</span>
                  {!canAccess && <Lock className="h-4 w-4 text-gray-400" />}
                </Link>
              );
            })}
            
            {/* Help & FAQ - Mobile */}
            <Link
              to="/organizer/help"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname === '/organizer/help'
                  ? 'bg-maroon text-white' 
                  : 'text-gray-700 hover:bg-cream-paper'
              }`}
            >
              <HelpCircle className="h-5 w-5" />
              <span className="font-medium">Help & FAQ</span>
            </Link>
            
            <div className="pt-4 border-t border-gray-200 mt-4">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 w-full"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex h-screen w-64 flex-col fixed left-0 top-0 bg-maroon">
        <div className="p-6 border-b border-white/10">
          <Link to="/organizer" className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shadow-lg">
              <span className="text-white font-heading font-bold text-xl">b8</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-white leading-tight">
                batchm8s
              </h1>
              <p className="text-xs text-white/60">Organizer Portal</p>
            </div>
          </Link>
          {selectedEvent && (
            <div className="mt-3 px-3 py-2 bg-gold/20 rounded-lg flex items-center gap-2">
              {logoUrl && (
                <img src={logoUrl} alt="Logo" className="h-8 w-8 rounded-full object-cover border border-gold/40 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-xs text-gold font-medium truncate">{selectedEvent.school_name}</p>
                <p className="text-xs text-white/60 truncate">{selectedEvent.event_name}</p>
              </div>
            </div>
          )}
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const showCartBadge = item.name === 'Cart' && cartCount > 0;
            const canAccess = canAccessRoute(item.href);
            return (
              <Link
                key={item.name}
                to={canAccess ? item.href : '#'}
                onClick={(e) => handleNavClick(e, item.href, canAccess)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive 
                    ? 'bg-white text-maroon shadow-md' 
                    : canAccess
                      ? 'text-white/80 hover:bg-white/10 hover:text-white'
                      : 'text-white/40 cursor-not-allowed'
                }`}
                data-testid={`organizer-nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className={`h-5 w-5 ${!canAccess ? 'opacity-50' : ''}`} />
                <span className={`font-medium ${!canAccess ? 'opacity-50' : ''}`}>{item.name}</span>
                {showCartBadge && canAccess && (
                  <span className="ml-auto bg-gold text-maroon text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {cartCount}
                  </span>
                )}
                {!canAccess && <Lock className="h-3 w-3 ml-auto text-white/40" />}
              </Link>
            );
          })}
          
          {/* Help & FAQ - At bottom of navigation */}
          <div className="pt-4 mt-4 border-t border-white/10">
            <Link
              to="/organizer/help"
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname === '/organizer/help'
                  ? 'bg-white text-maroon shadow-md' 
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
              data-testid="organizer-nav-help"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="font-medium">Help & FAQ</span>
            </Link>
          </div>
        </nav>

        {/* Upgrade CTA */}
        {selectedEvent && (
          <div className="px-4 pb-4">
            <Link
              to={`/pricing?event_id=${selectedEvent.id}`}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-gold to-amber-400 text-gray-900 hover:shadow-lg transition-all"
              data-testid="upgrade-btn"
            >
              <Crown className="h-5 w-5" />
              <div className="flex-1">
                <span className="font-semibold text-sm">Upgrade Reunion</span>
                <p className="text-xs text-gray-700">Unlock all features</p>
              </div>
            </Link>
          </div>
        )}

        {/* User section */}
        <div className="p-4 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-white/10 transition-all">
                <Avatar className="h-10 w-10 border-2 border-gold">
                  <AvatarImage src={profileImageUrl || user?.profile_photo} />
                  <AvatarFallback className="bg-gold text-maroon font-medium">
                    {getInitials(user?.first_name, user?.last_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="font-medium text-white truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <Badge className="text-xs bg-gold text-cream-dark">
                    Organizer
                  </Badge>
                </div>
                <ChevronDown className="h-4 w-4 text-white/60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/organizer/profile')}>
                <UserCircle className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0">
        {/* Top bar with notifications and refresh */}
        <div className="hidden md:flex items-center justify-end gap-2 p-4 border-b bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <NotificationBell />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.location.reload()}
            className="h-9 w-9 text-gray-500 hover:text-maroon"
            data-testid="refresh-page-btn"
            title="Refresh page"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-4 md:p-8 pb-24 md:pb-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default OrganizerLayout;
